/**
 * Fetch search asset validation config from the backend.
 * The backend is the single source of truth for validation rules.
 */

import { useQuery } from '@tanstack/react-query';
import client from './client';
import type { SearchFormatType } from '@/pages/asset-preview/components/search-details/search-config';

// ─── Types matching backend response shape ──────────────────────────────────

export interface BackendMediaSlotRule {
  min: number;
  max: number;
  label: string;
  is_youtube?: boolean;
  recommended_count?: number;
  recommended_text?: string;
  optional?: boolean;
}

export interface BackendTextFieldRule {
  min: number;
  max: number;
  char_limit: number;
}

export interface BackendValidationRules {
  one_headline_max_chars?: number;
  recommended_descriptions?: number;
  long_headline_min_warning?: number;
  video_recommended?: boolean;
}

export interface BackendCtaRule {
  min: number;
  max: number;
}

export interface BackendConditionalWarning {
  when: string;
  text_field?: string;
  media_slots?: string[];
  warn_key: string;
  message: string;
}

export interface BackendSearchMediaConfig {
  slots: Record<string, BackendMediaSlotRule>;
  shared_image_max?: number;
  conditional_warnings?: BackendConditionalWarning[];
  text_fields?: Record<string, BackendTextFieldRule>;
  validation_rules?: BackendValidationRules;
  final_url?: boolean;
  cta?: BackendCtaRule;
}

export type BackendValidationConfigResponse = Record<string, BackendSearchMediaConfig>;

// ─── Fetch + hook ───────────────────────────────────────────────────────────

async function fetchValidationConfig(): Promise<BackendValidationConfigResponse> {
  const { data } = await client.get<BackendValidationConfigResponse>(
    '/search-assets/validation-config',
  );
  return data;
}

/**
 * React-query hook to fetch the full validation config.
 * Stale for 24 hours since rules change very rarely.
 */
export function useSearchValidationConfig() {
  return useQuery({
    queryKey: ['search-validation-config'],
    queryFn: fetchValidationConfig,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 24 * 60 * 60 * 1000,
  });
}

/**
 * Get the validation config for a specific format type.
 */
export function useFormatValidationConfig(formatType: SearchFormatType | undefined) {
  const query = useSearchValidationConfig();
  const config = formatType && query.data ? query.data[formatType] : undefined;
  return { ...query, data: config };
}
